export type BuilderBlockStyleControlKind =
  | "alignment"
  | "color"
  | "dimension"
  | "media"
  | "number"
  | "select"
  | "text";

export type BuilderBlockStyleFieldDefinition = {
  blockTypes?: readonly string[];
  defaultValue?: number;
  key: string;
  kind: BuilderBlockStyleControlKind;
  max?: number;
  min?: number;
  options?: readonly string[];
  rootOnly?: boolean;
  step?: number;
  unit?: "" | "%" | "ms" | "px";
};

export type BuilderBlockStyleGroupDefinition = {
  fields: readonly BuilderBlockStyleFieldDefinition[];
  key:
    | "border"
    | "effects"
    | "layer"
    | "layout"
    | "motion"
    | "size"
    | "spacing"
    | "style"
    | "typography";
};

const field = (
  key: string,
  kind: BuilderBlockStyleControlKind,
  options?: readonly string[],
  config?: Pick<
    BuilderBlockStyleFieldDefinition,
    | "blockTypes"
    | "defaultValue"
    | "max"
    | "min"
    | "rootOnly"
    | "step"
    | "unit"
  >,
): BuilderBlockStyleFieldDefinition => ({
  key,
  kind,
  ...(options ? { options } : {}),
  ...config,
});

const dimension = (
  key: string,
  config: Pick<
    BuilderBlockStyleFieldDefinition,
    "defaultValue" | "max" | "min" | "step" | "unit"
  > & Partial<Pick<
    BuilderBlockStyleFieldDefinition,
    "blockTypes" | "rootOnly"
  >>,
): BuilderBlockStyleFieldDefinition => field(
  key,
  "dimension",
  undefined,
  config,
);

/**
 * Canonical visual controls for the shared Shop/Web builder.
 *
 * Module inspectors own labels and module data, but both consume this exact
 * field contract so an inner-content block never gains a Web-only or
 * Shop-only appearance model.
 */
export const BUILDER_BLOCK_STYLE_GROUPS = [
  {
    key: "style",
    fields: [
      field("backgroundColor", "color"),
      field("backgroundImage", "media"),
      field("backgroundOverlay", "color"),
      field("backgroundPosition", "select", [
        "center",
        "top",
        "right",
        "bottom",
        "left",
      ]),
      field("backgroundSize", "select", ["cover", "contain", "auto"]),
      field("textColor", "color"),
      field("socialIconColor", "color", undefined, {
        blockTypes: ["share", "socialLink", "socialMedia"],
        rootOnly: true,
      }),
      field("socialIconBackgroundColor", "color", undefined, {
        blockTypes: ["share", "socialLink", "socialMedia"],
        rootOnly: true,
      }),
    ],
  },
  {
    key: "layout",
    fields: [
      field("gridColumns", "number", undefined, {
        blockTypes: ["footer", "richContent"],
        min: 1,
        max: 24,
        rootOnly: true,
        step: 1,
      }),
      dimension("gridRowHeight", {
        blockTypes: ["footer", "richContent"],
        defaultValue: 48,
        max: 320,
        min: 16,
        rootOnly: true,
        step: 4,
        unit: "px",
      }),
      field("verticalAlign", "alignment", [
        "",
        "top",
        "center",
        "bottom",
      ]),
    ],
  },
  {
    key: "typography",
    fields: [
      field("fontFamily", "select", [
        "",
        "var(--site-heading-font)",
        "var(--site-body-font)",
        "Inter",
        "Georgia",
        "Arial",
      ]),
      dimension("fontSize", {
        defaultValue: 24,
        max: 280,
        min: 8,
        step: 2,
        unit: "px",
      }),
      field("fontStyle", "select", ["", "normal", "italic"]),
      field("fontWeight", "select", [
        "",
        "300",
        "400",
        "500",
        "600",
        "650",
        "700",
        "800",
        "900",
      ]),
      dimension("lineHeight", {
        defaultValue: 1.5,
        max: 3,
        min: 0.8,
        step: 0.05,
        unit: "",
      }),
      dimension("letterSpacing", {
        defaultValue: 0,
        max: 48,
        min: -12,
        step: 0.5,
        unit: "px",
      }),
      field("textAlign", "alignment", ["left", "center", "right"]),
      field("textDecoration", "select", [
        "",
        "none",
        "underline",
        "line-through",
      ]),
      field("textTransform", "select", [
        "",
        "none",
        "uppercase",
        "lowercase",
        "capitalize",
      ]),
      field("whiteSpace", "select", [
        "",
        "normal",
        "nowrap",
        "pre-line",
        "pre-wrap",
      ]),
    ],
  },
  {
    key: "effects",
    fields: [
      field("boxShadow", "select", [
        "",
        "none",
        "soft",
        "medium",
        "strong",
        "glow",
      ]),
      dimension("backdropBlur", {
        defaultValue: 12,
        max: 64,
        min: 0,
        step: 1,
        unit: "px",
      }),
      field("transform", "text"),
      field("overflow", "select", [
        "",
        "visible",
        "hidden",
        "auto",
        "scroll",
      ]),
    ],
  },
  {
    key: "size",
    fields: [
      dimension("width", {
        defaultValue: 320,
        max: 1800,
        min: 24,
        step: 8,
        unit: "px",
      }),
      dimension("maxWidth", {
        defaultValue: 1200,
        max: 1800,
        min: 24,
        step: 8,
        unit: "px",
      }),
      dimension("height", {
        defaultValue: 160,
        max: 1200,
        min: 0,
        step: 8,
        unit: "px",
      }),
      dimension("minHeight", {
        defaultValue: 160,
        max: 1200,
        min: 0,
        step: 8,
        unit: "px",
      }),
      field("position", "select", [
        "",
        "relative",
        "absolute",
        "fixed",
        "sticky",
      ]),
      dimension("top", {
        defaultValue: 0,
        max: 400,
        min: -400,
        step: 4,
        unit: "px",
      }),
    ],
  },
  {
    key: "border",
    fields: [
      field("borderColor", "color"),
      field("borderStyle", "select", [
        "",
        "none",
        "solid",
        "dashed",
        "dotted",
        "double",
      ]),
      dimension("borderWidth", {
        defaultValue: 1,
        max: 20,
        min: 0,
        step: 1,
        unit: "px",
      }),
      dimension("borderRadius", {
        defaultValue: 16,
        max: 160,
        min: 0,
        step: 1,
        unit: "px",
      }),
    ],
  },
  {
    key: "spacing",
    fields: [
      dimension("padding", {
        defaultValue: 24,
        max: 280,
        min: 0,
        step: 4,
        unit: "px",
      }),
      dimension("paddingTop", {
        defaultValue: 24,
        max: 280,
        min: 0,
        step: 4,
        unit: "px",
      }),
      dimension("paddingRight", {
        defaultValue: 24,
        max: 280,
        min: 0,
        step: 4,
        unit: "px",
      }),
      dimension("paddingBottom", {
        defaultValue: 24,
        max: 280,
        min: 0,
        step: 4,
        unit: "px",
      }),
      dimension("paddingLeft", {
        defaultValue: 24,
        max: 280,
        min: 0,
        step: 4,
        unit: "px",
      }),
      dimension("marginTop", {
        defaultValue: 0,
        max: 280,
        min: -280,
        step: 4,
        unit: "px",
      }),
      dimension("marginRight", {
        defaultValue: 0,
        max: 280,
        min: -280,
        step: 4,
        unit: "px",
      }),
      dimension("marginBottom", {
        defaultValue: 0,
        max: 280,
        min: -280,
        step: 4,
        unit: "px",
      }),
      dimension("marginLeft", {
        defaultValue: 0,
        max: 280,
        min: -280,
        step: 4,
        unit: "px",
      }),
      dimension("gap", {
        defaultValue: 24,
        max: 280,
        min: 0,
        step: 4,
        unit: "px",
      }),
    ],
  },
  {
    key: "motion",
    fields: [
      field("hoverEffect", "select", [
        "",
        "blur",
        "brighten",
        "darken",
        "glow",
        "lift-soft",
        "lift",
        "outline",
        "rotate",
        "scale",
        "scale-sm",
        "shadow",
        "skew",
        "slide-right",
        "underline",
      ]),
      dimension("hoverDuration", {
        defaultValue: 520,
        max: 1600,
        min: 80,
        step: 20,
        unit: "ms",
      }),
      field("hoverTextColor", "color"),
      field("hoverBackgroundColor", "color"),
      field("hoverBorderColor", "color"),
      field("animationMode", "select", ["", "appearance", "scroll"]),
      field("animationEffect", "select", [
        "",
        "fade",
        "zoom",
        "flip",
        "rotate",
        "blur",
        "bounce",
        "float",
        "clip",
        "reveal",
      ]),
      field("animationDirection", "select", [
        "",
        "up",
        "down",
        "left",
        "right",
        "x",
        "y",
        "in",
        "out",
      ]),
      field("animationTrigger", "select", ["once", "always"]),
      dimension("animationDuration", {
        defaultValue: 700,
        max: 2400,
        min: 150,
        step: 50,
        unit: "ms",
      }),
      dimension("animationStartAfter", {
        defaultValue: 0,
        max: 2000,
        min: 0,
        step: 50,
        unit: "ms",
      }),
      dimension("animationScrollStart", {
        defaultValue: 0,
        max: 100,
        min: 0,
        step: 5,
        unit: "%",
      }),
      dimension("animationScrollEnd", {
        defaultValue: 60,
        max: 100,
        min: 0,
        step: 5,
        unit: "%",
      }),
    ],
  },
  {
    key: "layer",
    fields: [
      dimension("opacity", {
        defaultValue: 1,
        min: 0,
        max: 1,
        step: 0.05,
        unit: "",
      }),
      field("zIndex", "number", undefined, {
        step: 1,
      }),
    ],
  },
] as const satisfies readonly BuilderBlockStyleGroupDefinition[];

export const BUILDER_BLOCK_STYLE_FIELD_KEYS = [
  ...new Set(
    BUILDER_BLOCK_STYLE_GROUPS.flatMap((group) => (
      group.fields.map(({ key }) => key)
    )),
  ),
] as readonly string[];

type BuilderBlockStyleContext = {
  layoutContainer: boolean;
  styleCapabilities: readonly string[];
  type: string;
};

function groupMatchesCapabilities(
  group: BuilderBlockStyleGroupDefinition,
  context: BuilderBlockStyleContext,
): boolean {
  const capabilities = new Set(context.styleCapabilities);
  const hasAny = (...values: string[]) => (
    values.some((value) => capabilities.has(value))
  );

  if (group.key === "layout") {
    return context.layoutContainer || hasAny("layout");
  }

  if (group.key === "typography") {
    return hasAny("button", "typography");
  }

  if (group.key === "effects") {
    return hasAny("button", "media", "surface");
  }

  if (group.key === "size") {
    return context.layoutContainer
      || hasAny("button", "layout", "media", "size");
  }

  if (group.key === "border") {
    return hasAny("border", "button", "radius", "surface");
  }

  if (group.key === "spacing") {
    return hasAny("gap", "spacing");
  }

  if (group.key === "style") {
    return context.layoutContainer
      || hasAny("button", "color", "media", "surface");
  }

  // Motion and layer values are rendered by the neutral block style runtime
  // for every block type, so they remain universally available.
  return true;
}

export function builderBlockStyleFieldsForContext(
  group: BuilderBlockStyleGroupDefinition,
  context: BuilderBlockStyleContext,
  part: string | null,
): BuilderBlockStyleFieldDefinition[] {
  if (!groupMatchesCapabilities(group, context)) {
    return [];
  }

  return group.fields.filter((candidate) => {
    if (part !== null && candidate.rootOnly) {
      return false;
    }

    if (
      (context.type === "image" || context.type === "media")
      && candidate.key === "backgroundImage"
    ) {
      return false;
    }

    if (context.type === "icon" && candidate.key === "fontSize") {
      return false;
    }

    return !candidate.blockTypes
      || candidate.blockTypes.includes(context.type);
  });
}
